import React, { useState, useEffect, useRef } from "react";
import { Search, Plus } from "lucide-react";
import { useDebounce } from "../../hooks";
import { toast } from "react-toastify";
import { batchAPI, purchaseAPI } from "../../services";
import { handleFormattedValidationErrors } from "../../utils/errorHandlers";
import BatchForm from "./BatchForm";
import StockForm from "./StockForm";

import TotalProductsIcon from "../../assets/total-product.svg";
import TotalStockIcon from "../../assets/total-stock.svg";
import TotalInventoryValueIcon from "../../assets/total-inventory.svg";

import EditIcon from "../../assets/icons/EditIcon";
import EyeIcon from "../../assets/icons/EyeIcon";
import PlusIcon from "../../assets/icons/PlusIcon";
import RedDeleteIcon from "../../assets/icons/RedDeleteIcon";
import NoDataIcon from "../../assets/icons/NoDataIcon";
import DeleteIcon from "../../assets/delete-modal-icon.svg";
import { useSelector } from "react-redux";

import "../../style/batches.css";

const BatchView = ({
  onViewBatch,
  suppliers = [],
  products = [],
  loading = false,
  onRefresh,
  batches = [],
  totalCount = 0,
  currentPage: parentCurrentPage = 1,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingBatch, setEditingBatch] = useState(null);
  const [batchStats, setBatchStats] = useState({
    totalBatches: 0,
    totalItems: 0,
    totalValue: 0,
  });

  // Debounce search term to avoid too many API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const [activeBatchId, setActiveBatchId] = useState(null);
  const [showStockForm, setShowStockForm] = useState(false);
  const [editingStock, setEditingStock] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingBatch, setDeletingBatch] = useState(null);
  const { isPartsURL, isAccessoriesURL, isGodownURL } = useSelector((state) => state.domain);
  // Use parent's current page
  const [currentPage, setCurrentPage] = useState(parentCurrentPage);

  useEffect(() => {
    setCurrentPage(parentCurrentPage);
  }, [parentCurrentPage]);
  const [itemsPerPage] = useState(25);

  // Track if stats have been fetched to prevent duplicate calls
  const hasFetchedStats = useRef(false);

  // Fetch batch statistics from API
  const fetchBatchStats = async () => {
    try {
      const response = await batchAPI.getBatchCount();
      if (response) {
        setBatchStats({
          totalBatches: response.total_batches || response.count || 0,
          totalItems: response.total_items || response.total_quantity || 0,
          totalValue: response.total_value || response.total_amount || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching batch stats:", error);
      // Fallback to calculating from current batches
      setBatchStats({
        totalBatches: totalCount,
        totalItems: batches.reduce((sum, b) => sum + (b.quantity || 0), 0),
        totalValue: batches.reduce((sum, b) => sum + (b.amount || 0), 0),
      });
    }
  };

  // Load batch stats on component mount
  useEffect(() => {
    // Prevent duplicate API calls in React StrictMode
    if (hasFetchedStats.current) return;
    hasFetchedStats.current = true;

    fetchBatchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset to first page when search term changes and trigger API search
  useEffect(() => {
    // Only trigger search if searchTerm has actually changed (not on initial mount with empty string)
    if (debouncedSearchTerm !== "" || searchTerm !== "") {
      setCurrentPage(1);
      if (onRefresh) {
        onRefresh(debouncedSearchTerm, 1);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  // Use API stats if available, otherwise fallback to calculated values
  const totalBatches = batchStats.totalBatches || totalCount;
  const totalItems =
    batchStats.totalItems ||
    batches.reduce((sum, b) => sum + (b.quantity || 0), 0);
  const totalValue =
    batchStats.totalValue ||
    batches.reduce((sum, b) => sum + (b.amount || 0), 0);

  // Use API pagination instead of local pagination
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  
  const paginatedBatches = batches;

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    if (onRefresh) {
      onRefresh(debouncedSearchTerm, newPage);
    }
  };

  const handleAddBatch = async (batchData) => {
    try {
      // Create the batch via API
      await batchAPI.createBatch(batchData);

      // Refresh the batch list and stats
      if (onRefresh) await onRefresh();
      await fetchBatchStats(); // Refresh stats after adding batch
      setShowForm(false);
      toast.success("Batch created successfully!");
    } catch (error) {
      console.error("Error creating batch:", error);
      handleFormattedValidationErrors(
        error,
        "Failed to create batch. Please try again."
      );
      setShowForm(false);
    }
  };

  const handleEditBatch = async (updatedBatch) => {
    try {
      await batchAPI.updateBatch(updatedBatch.id, updatedBatch);
      if (onRefresh) await onRefresh();
      await fetchBatchStats(); // Refresh stats after updating batch
      setEditingBatch(null);
      setShowForm(false);
      toast.success("Batch updated successfully!");
    } catch (error) {
      console.error("Error updating batch:", error);
      handleFormattedValidationErrors(
        error,
        "Failed to update batch. Please try again."
      );
    }
  };

  const handleAddStock = async (batchId, stockData) => {
    try {
      await purchaseAPI.createPurchase({
        product: stockData.productId,
        batch: batchId,
        quantity: stockData.quantity,
        selling_price: stockData.sellingPrice,
        buying_price: stockData.buyingPrice,
        record_point: 10,
        available_qty: stockData.quantity,
      });

      toast.success("Stock added and purchase record created!");

      // Refresh the batches list and stats to show updated data
      if (onRefresh) {
        await onRefresh();
      }
      await fetchBatchStats(); // Refresh stats after adding stock
    } catch (error) {
      console.error("Error adding stock:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to add stock. Please try again.";
      toast.error(errorMessage);
    }
  };

  const handleEditStock = (batchId, updatedStock) => {
    // Handle stock editing if needed
  };

  const handleDeleteBatch = async (id) => {
    try {
      await batchAPI.deleteBatch(id);
      toast.success("Batch deleted successfully!");
      if (onRefresh) await onRefresh();
      await fetchBatchStats();
      setShowDeleteModal(false);
      setDeletingBatch(null);
    } catch (error) {
      console.error("Error deleting batch:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to delete batch. Please try again.";
      toast.error(errorMessage);
    }
  };

  return (
    <div
      className="card rounded-4 p-4 border-0 batches-layout"
      style={{ borderRadius: "14px", background: "#fff", height: "100%" }}
    >
      <div className="batches-top">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="batches-title">Batches</h1>
            <p className="batches-subtitle">Manage your batches</p>
          </div>
          <button
            className={`${
              isPartsURL
                ? "add-brand-btn-parts"
                : isAccessoriesURL
                ? "add-brand-btn-acc"
                : isGodownURL
                ? "add-brand-btn-godown"
                : "add-brand-btn"
            }`}
            onClick={() => {
              setEditingBatch(null);
              setShowForm(true);
            }}
          >
            <Plus className="add-brand-icon" /> Add Batch
          </button>
        </div>

        {/* Stats */}
        <div className="row mb-4">
          <div className="col-md-3">
            <div
              className="p-3 d-flex align-items-center justify-content-between brands-summary-card"
              style={{
                border: "1px solid #EAEEF2",
                height: "92px",
                borderRadius: "10px",
              }}
            >
              <div className="d-flex flex-column align-items-start justify-content-center gap-1">
                <small className="text-muted">Total Batch</small>
                <h5 className="mb-0">{totalBatches}</h5>
              </div>
              <img src={TotalProductsIcon} alt="Total Products" />
            </div>
          </div>

          <div className="col-md-3">
            <div
              className="p-3 d-flex align-items-center justify-content-between brands-summary-card"
              style={{
                border: "1px solid #EAEEF2",
                height: "92px",
                borderRadius: "10px",
              }}
            >
              <div className="d-flex flex-column align-items-start justify-content-center gap-1">
                <small className="text-muted">Total Items</small>
                <h5 className="mb-0">{totalItems}</h5>
              </div>
              <img src={TotalStockIcon} alt="Total Stock" />
            </div>
          </div>

          <div className="col-md-3">
            <div
              className="p-3 d-flex align-items-center justify-content-between brands-summary-card"
              style={{
                border: "1px solid #EAEEF2",
                height: "92px",
                borderRadius: "10px",
              }}
            >
              <div className="d-flex flex-column align-items-start justify-content-center gap-1">
                <small className="text-muted">Total Value</small>
                <h5 className="mb-0">€{totalValue.toFixed(2)}</h5>
              </div>
              <img src={TotalInventoryValueIcon} alt="Total Inventory Value" />
            </div>
          </div>
        </div>

        {/* Search */}
        <div
          className="d-flex align-items-center mb-3 gap-3"
          // style={{ width: "548px" }}
        >
          <div className="position-relative flex-grow-1">
            <input
              type="text"
              className={`${
                isPartsURL
                  ? "brand-search-input-parts"
                  : isAccessoriesURL
                  ? "brand-search-input-acc"
                  : isGodownURL
                  ? "brand-search-input-godown"
                  : "brand-search-input"
              } text-search`}
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search
              size={16}
              className="position-absolute top-50 start-0 translate-middle-y ms-2"
              style={{ color: "#8C959F" }}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive batches-table-wrapper">
        <table className="batches-table">
          <thead>
            <tr>
              <th>BATCH NO.</th>
              <th>SUPPLIER</th>
              <th>INVOICE NO.</th>
              <th>TOTAL QTY.</th>
              <th>AMOUNT</th>
              <th>DATE</th>
              <th style={{ textAlign: "center" }}>ACTION</th>
            </tr>
          </thead>
          <tbody>
            {paginatedBatches.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-products-message">
                  <div
                    className=""
                    style={{
                      marginBottom: "4rem",
                      marginTop: "4rem",
                    }}
                  >
                    <NoDataIcon />
                    <h3 style={{ marginTop: "2rem" }}>No Batches Found</h3>
                  </div>
                </td>
              </tr>
            ) : (
              paginatedBatches.map((batch) => {
                // Use vendor_details from API response instead of looking up in suppliers array
                const supplier = batch.vendor_details;
                return (
                  <tr key={batch.id}>
                    <td>{batch.batch_number}</td>
                    <td>{supplier ? supplier.name : "—"}</td>
                    <td>{batch.invoice_number || "—"}</td>
                    <td>{batch.quantity || "—"}</td>
                    <td className="price-cell">
                      €{parseFloat(batch.amount || 0).toFixed(2)}
                    </td>
                    <td className="date-cell">
                      {batch.created_at
                        ? new Date(batch.created_at).toLocaleDateString()
                        : "—"}
                    </td>
                    <td style={{ borderBottom: "1px solid #f0f2f5" }}>
                      <div className="action-buttons">
                        <button
                          className={`${
                            isPartsURL
                              ? "action-btn-parts view"
                              : isAccessoriesURL
                              ? "action-btn-acc view"
                              : isGodownURL
                              ? "action-btn-godown view"
                              : "action-btn view"
                          }`}
                          onClick={() => onViewBatch(batch)}
                        >
                          <EyeIcon
                            fill={
                              isPartsURL
                                ? "var(--primary-color-parts)"
                                : isAccessoriesURL
                                ? "var(--primary-color-acc)"
                                : isGodownURL
                                ? "var(--primary-color-godown)"
                                : "var(--primary-color-mobile)"
                            }
                          />
                        </button>
                        <button
                          className={`${
                            isPartsURL
                              ? "action-btn-parts view"
                              : isAccessoriesURL
                              ? "action-btn-acc view"
                              : isGodownURL
                              ? "action-btn-godown view"
                              : "action-btn view"
                          }`}
                          onClick={() => {
                            // Convert API data to form format
                            const editableBatch = {
                              ...batch,
                              invoiceNumber: batch.invoice_number,
                              totalQuantity: batch.quantity,
                              supplierId: batch.vendor,
                              description: batch.description || "",
                              amount: batch.amount,
                            };
                            setEditingBatch(editableBatch);
                            setShowForm(true);
                          }}
                        >
                          <EditIcon
                            fill={
                              isPartsURL
                                ? "var(--primary-color-parts)"
                                : isAccessoriesURL
                                ? "var(--primary-color-acc)"
                                : isGodownURL
                                ? "var(--primary-color-godown)"
                                : "var(--primary-color-mobile)"
                            }
                          />
                        </button>
                        <button
                          className={`${
                            isPartsURL
                              ? "action-btn-parts view"
                              : isAccessoriesURL
                              ? "action-btn-acc view"
                              : isGodownURL
                              ? "action-btn-godown view"
                              : "action-btn view"
                          }`}
                          onClick={() => {
                            setActiveBatchId(batch.id);
                            setEditingStock(null);
                            setShowStockForm(true);
                          }}
                          disabled={isGodownURL && batch.is_published}
                          style={{
                            opacity: isGodownURL && batch.is_published ? 0.5 : 1,
                            cursor: isGodownURL && batch.is_published ? "not-allowed" : "pointer"
                          }}
                        >
                          <PlusIcon
                            color={
                              isPartsURL
                                ? "var(--primary-color-parts)"
                                : isAccessoriesURL
                                ? "var(--primary-color-acc)"
                                : isGodownURL
                                ? "var(--primary-color-godown)"
                                : "var(--primary-color-mobile)"
                            }
                          />
                        </button>
                        <button
                          className="action-btn delete"
                          onClick={() => {
                            setDeletingBatch(batch);
                            setShowDeleteModal(true);
                          }}
                        >
                          <RedDeleteIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination - Only show when there are more than 25 items */}
      {totalCount > 25 && (
        <div className="pagination-container batches-pagination-sticky">
          <div className="pagination-info">
            Page {currentPage} of {totalPages}
          </div>
          <div className="pagination-controls">
            <button
              className={`${
                isPartsURL
                  ? "pagination-btn-parts"
                  : isAccessoriesURL
                  ? "pagination-btn-acc"
                  : isGodownURL
                  ? "pagination-btn-godown"
                  : "pagination-btn"
              }`}
              disabled={currentPage === 1}
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
            >
              Previous
            </button>
            <button
              className={`${
                isPartsURL
                  ? "pagination-btn-next-parts"
                  : isAccessoriesURL
                  ? "pagination-btn-next-acc"
                  : isGodownURL
                  ? "pagination-btn-next-godown"
                  : "pagination-btn-next"
              }`}
              disabled={currentPage >= totalPages && batches.length < itemsPerPage}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      {showForm && (
        <BatchForm
          onSubmit={editingBatch ? handleEditBatch : handleAddBatch}
          onCancel={() => {
            setShowForm(false);
            setEditingBatch(null);
          }}
          editingBatch={editingBatch}
          suppliers={suppliers}
        />
      )}

      {showStockForm && (
        <StockForm
          onSubmit={(stockData) => {
            if (editingStock) {
              handleEditStock(activeBatchId, stockData);
            } else {
              handleAddStock(activeBatchId, stockData);
            }
            setShowStockForm(false);
            setEditingStock(null);
          }}
          onCancel={() => {
            setShowStockForm(false);
            setEditingStock(null);
          }}
          editingStock={editingStock}
          products={products}
          suppliers={suppliers}
          allBatches={batches}
        />
      )}

      {/* Delete Modal */}
      {showDeleteModal && deletingBatch && (
        <>
          <div className="modal-overlay">
            <div className="delete-modal">
              <div className="modal-icon">
                <img src={DeleteIcon} alt="Delete" />
              </div>
              <h3 className="modal-title">Delete Batch</h3>
              <p className="modal-message">
                Are you sure you want to delete batch{" "}
                <b>{deletingBatch.batch_number}</b>?
                <br />
                This action cannot be undone.
              </p>
              <div className="modal-actions">
                <button
                  className="modal-cancel-btn"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletingBatch(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="modal-delete-btn"
                  onClick={() => handleDeleteBatch(deletingBatch.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BatchView;
